export const PDF_ALLOWED_TYPES_LABEL = 'pdf';
export const PDF_EMPTY_FILE_MESSAGE = 'No file was uploaded.';
export const PDF_FILE_TOO_LARGE_MESSAGE = 'File is Too Large';

const ALLOWED_EXTENSION = '.pdf';
// ISO 32000 allows up to 1024 leading bytes before %PDF.
const PDF_HEADER_SEARCH_LIMIT = 1024;
const PDF_HEADER = [0x25, 0x50, 0x44, 0x46];

const DANGEROUS_EXTENSIONS = new Set([
  '.php', '.php3', '.php4', '.php5', '.phtml', '.phar',
  '.exe', '.dll', '.com', '.bat', '.cmd', '.msi', '.scr', '.inf',
  '.ps1', '.psm1', '.psd1', '.vbs', '.vbe', '.js', '.jse', '.jar',
  '.asp', '.aspx', '.ashx', '.asmx', '.jsp', '.jspx',
  '.sh', '.bash', '.zsh', '.csh',
  '.html', '.htm', '.xhtml', '.svg', '.xml',
  '.htaccess', '.config', '.cer',
  '.app', '.dmg', '.pkg', '.deb', '.rpm',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.tif', '.tiff',
  '.zip', '.rar', '.7z', '.gz', '.tar',
  '.sql', '.sqlite', '.db'
]);

export function formatDisallowedTypeMessage(detectedContentType: string): string {
  const type = (detectedContentType || 'unknown').trim();
  return `Type '${type} is not allowed. Allowed types: '${PDF_ALLOWED_TYPES_LABEL}`;
}

function getExtensionChain(fileName: string): string[] {
  const extensions: string[] = [];
  let name = fileName;
  while (true) {
    const dotIndex = name.lastIndexOf('.');
    if (dotIndex <= 0) {
      break;
    }
    const extension = name.substring(dotIndex).toLowerCase();
    extensions.unshift(extension);
    name = name.substring(0, dotIndex);
  }
  return extensions;
}

function containsEmbeddedDangerousExtension(baseName: string): boolean {
  if (!baseName) {
    return false;
  }
  for (const dangerous of DANGEROUS_EXTENSIONS) {
    const bare = dangerous.replace(/^\./, '');
    if (
      baseName.toLowerCase().endsWith(dangerous) ||
      baseName.toLowerCase().includes(`${dangerous}.`) ||
      baseName.toLowerCase().includes(`.${bare}.`)
    ) {
      return true;
    }
  }
  return false;
}

export function validatePdfFileName(fileName: string): string | null {
  if (!fileName || !fileName.trim()) {
    return formatDisallowedTypeMessage('unknown');
  }

  const safeName = fileName.split(/[\\/]/).pop() || fileName;
  if (safeName.includes('..') || safeName.includes('\0')) {
    return formatDisallowedTypeMessage('invalid-filename');
  }

  const extensionChain = getExtensionChain(safeName);
  if (
    extensionChain.length !== 1 ||
    extensionChain[0].toLowerCase() !== ALLOWED_EXTENSION
  ) {
    const disguised = extensionChain.find(e => e.toLowerCase() !== ALLOWED_EXTENSION);
    return formatDisallowedTypeMessage(
      disguised ? `application/x-disguised${disguised}` : 'invalid-extension'
    );
  }

  for (const extension of extensionChain) {
    if (DANGEROUS_EXTENSIONS.has(extension.toLowerCase())) {
      return formatDisallowedTypeMessage(`application/x-disguised${extension}`);
    }
  }

  const baseName = safeName.replace(/\.pdf$/i, '');
  if (containsEmbeddedDangerousExtension(baseName)) {
    return formatDisallowedTypeMessage('application/x-disguised-double-extension');
  }

  return null;
}

export function validatePdfFileSize(file: File, maxBytes: number): string | null {
  if (!file || !file.size) {
    return PDF_EMPTY_FILE_MESSAGE;
  }
  if (maxBytes > 0 && file.size > maxBytes) {
    return PDF_FILE_TOO_LARGE_MESSAGE;
  }
  return null;
}

function readFileHeader(file: File, length: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file.slice(0, length));
  });
}

function indexOfBytes(source: Uint8Array, pattern: number[], scanLength: number): number {
  const limit = Math.min(scanLength, source.length) - pattern.length;
  for (let i = 0; i <= limit; i++) {
    let matched = true;
    for (let j = 0; j < pattern.length; j++) {
      if (source[i + j] !== pattern[j]) {
        matched = false;
        break;
      }
    }
    if (matched) {
      return i;
    }
  }
  return -1;
}

function findPdfHeaderOffset(header: Uint8Array): number {
  if (!header || header.length < 4) {
    return -1;
  }

  const searchLimit = Math.min(header.length - 4, PDF_HEADER_SEARCH_LIMIT);
  for (let i = 0; i <= searchLimit; i++) {
    if (
      header[i] === PDF_HEADER[0] &&
      header[i + 1] === PDF_HEADER[1] &&
      header[i + 2] === PDF_HEADER[2] &&
      header[i + 3] === PDF_HEADER[3]
    ) {
      return i;
    }
  }

  return -1;
}

export async function validatePdfFileContent(file: File): Promise<string | null> {
  const nameError = validatePdfFileName(file.name);
  if (nameError) {
    return nameError;
  }

  if (!file.size) {
    return PDF_EMPTY_FILE_MESSAGE;
  }

  const header = await readFileHeader(file, 8192);
  const pdfOffset = findPdfHeaderOffset(header);
  if (pdfOffset < 0) {
    return formatDisallowedTypeMessage(file.type || 'application/octet-stream');
  }

  // Only the bytes before %PDF. Scanning the PDF body flags binary streams.
  const dangerousPatterns: Array<{ bytes: number[]; type: string }> = [
    { bytes: [0x4d, 0x5a], type: 'application/x-msdownload' },
    { bytes: [60, 63, 112, 104, 112], type: 'application/x-php' },
    { bytes: [60, 115, 99, 114, 105, 112, 116], type: 'text/javascript' },
    { bytes: [60, 33, 68, 79, 67, 84, 89, 80, 69, 32, 104, 116, 109, 108], type: 'text/html' },
    { bytes: [60, 33, 100, 111, 99, 116, 121, 112, 101, 32, 104, 116, 109, 108], type: 'text/html' },
    { bytes: [35, 33, 47], type: 'application/x-executable' }
  ];

  if (pdfOffset > 0) {
    for (const pattern of dangerousPatterns) {
      if (indexOfBytes(header, pattern.bytes, pdfOffset) >= 0) {
        return formatDisallowedTypeMessage(pattern.type);
      }
    }
  }

  if (
    file.type &&
    file.type !== 'application/pdf' &&
    file.type !== 'application/x-pdf' &&
    file.type !== 'application/octet-stream' &&
    file.type !== 'binary/octet-stream'
  ) {
    return formatDisallowedTypeMessage(file.type);
  }

  return null;
}

export async function validatePdfFileBeforeUpload(file: File, maxBytes?: number): Promise<string | null> {
  if (maxBytes && maxBytes > 0) {
    const sizeError = validatePdfFileSize(file, maxBytes);
    if (sizeError) {
      return sizeError;
    }
  }

  return validatePdfFileContent(file);
}

export function parseUploadApiError(response: string, status: number): string {
  if (response) {
    try {
      const body = JSON.parse(response);
      if (body && body.Message) {
        return body.Message;
      }
    } catch {
      if (response.trim()) {
        return response.trim();
      }
    }
  }

  if (status === 422) {
    return formatDisallowedTypeMessage('unknown');
  }

  return 'Unable to upload the file. Please try again.';
}
